import { FC, useState, useEffect, Fragment } from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material'

import { AddOtherChargesRequest, Challan, Charges, MATERIAL_LOSS_CHARGE_LABEL, WEIGHT_CHARGE_ITEM_TYPE } from '../../../services'

// Components
import { AppDialog, ChallanSection } from '../../../components'
import AddOtherCharges from './AddOtherCharges'
import PreviewAddOtherCharges from './PreviewAddOtherCharges'

// Utils
import { getFormattedDate } from '../../../utils'

// Redux
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  addOtherChargesResetAction,
  addOtherChargesAction,
} from '../../../store/items'
import { showAlertAction } from '../../../store/alerts'
import { fetchSlipsListAction } from '../../../store/slips'
import SiteAutocomplete from '../../../../src/components/SitesAutoComplete/SiteAutocomplete'
import { SiteDto } from '../../../../src/components/SitesAutoComplete/Services'
import { useCommandPaletteActions } from '../../../contexts/CommandPaletteContext'

export interface OtherChargesProps {
  onClose?: (site?: SiteDto) => void
  bill_id: number
  customer_id: number
  title: string
  isQuotation: boolean
  bill?: any // Add bill prop to access isGSTEnabled or use site.isGSTEnabled
}

const OtherCharges: FC<OtherChargesProps> = ({
  onClose,
  bill_id,
  customer_id,
  title,
  isQuotation,
  bill,
}) => {
  const dispatchAction = useAppDispatch()
  const { openNewSite } = useCommandPaletteActions()
  const { success: addOtherChargesSuccess, message } = useAppSelector(
    (state) => state.addOtherCharges
  )
  const [billId, setBillId] = useState<number>(0)
  const [customerId, setCustomerId] = useState<number>(0)
  const [site, setSite] = useState<SiteDto | null>(null)

  const [charges, setCharges] = useState<Charges[]>([])
  const [date, setDate] = useState<Date | null>(null)
  const [isClose, setClosePopup] = useState<boolean>(false)
  const [selectedChallan, setSelectedChallan] = useState<Challan>(null)
  const [chargeType, setChargeType] = useState<string>('Other Charge')
  const [dummyCharge, setDummyCharge] = useState<Charges | null>(null)
  const [userDefinedChallanNumber, setDefinedChallanNumber] = useState('')
  const [addOtherChargesRequest, setAddOtherChargesRequest] =
    useState<AddOtherChargesRequest | null>(null)
  const [readyToSubmit, setReadyToSubmit] = useState<boolean>(true)
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)
  const chargeTypes = ['Other Charge', 'Security Deposit', 'Security Refund', MATERIAL_LOSS_CHARGE_LABEL]

  // Calculate isGSTEnabled from either bill or site
  const isGSTEnabled = bill?.isGSTEnabled || site?.isGSTEnabled || false

  useEffect(() => {
    const formatedDate = getFormattedDate(new Date(date))

    setAddOtherChargesRequest((request) => {
      // Remove the old date from request if user is not selected the date
      if (!date && request?.date) {
        delete request.date
      }
      return {
        ...request,
        ...(charges && { charges }),
        ...(customerId && { customerId: customerId }),
        ...(billId && { billId: billId }),
        ...(date && { date: formatedDate }),
        challanNo: userDefinedChallanNumber,
        challanId: selectedChallan?.id,
        isQuotation: isQuotation,
      }
    })
  }, [
    charges,
    date,
    customerId,
    billId,
    selectedChallan,
    userDefinedChallanNumber,
  ])

  useEffect(() => {
    if(bill_id) {
      setBillId(bill_id)
    }
    if(customer_id) {
      setCustomerId(customer_id)
    }
  
  }, [bill_id, customer_id])

  useEffect(() => {
    if (addOtherChargesSuccess) {
      setCharges([])
      setDate(new Date())
      setAddOtherChargesRequest(null)
      dispatchAction(
        showAlertAction({
          message: 'Other Charges Added Successfully',
          severity: 'success',
        })
      )
      onClose(site)
      dispatchAction(fetchSlipsListAction(billId))
      dispatchAction(addOtherChargesResetAction())
    } else if (message) {
      dispatchAction(
        showAlertAction({
          message: message,
          severity: 'error',
        })
      )
      dispatchAction(addOtherChargesResetAction())
    }
  }, [addOtherChargesSuccess, dispatchAction, message])

  useEffect(() => {
    console.log(charges)
  }, [charges])

  const submitHandle = () => {
    if (isQuotation) {
      addOtherChargesRequest.date = getFormattedDate(new Date())
      addOtherChargesRequest.challanNo = '0'
    }
    const { charges, customerId, date, billId, challanNo, challanId } =
      addOtherChargesRequest
    if (
      customerId &&
      billId &&
      charges.length > 0 &&
      (challanId || (challanNo?.trim() && date))
    ) {
      dispatchAction(addOtherChargesAction(addOtherChargesRequest))
    } else {
      dispatchAction(
        showAlertAction({
          message: 'Please fill the manadatory fields.',
          severity: 'error',
        })
      )
    }
  }

  return (
    <AppDialog
      id="add-other-charges"
      title={`Add Other Charges (${title})`}
      submitButtonText="Submit"
      onClose={onClose}
      onSubmit={submitHandle}
      close={isClose}
      maxWidth="md"
    >
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
      >
         {(!bill_id || bill_id === 0) && (
           <Grid item xs={12}>
             <SiteAutocomplete 
               onSiteSelected={(site?: SiteDto) => {
                 setBillId(site?.id)
                 setCustomerId(site?.customerId)
                 setSite(site)
               }}
               onAddNewSite={() => {
                 if (openNewSite) {
                   openNewSite()
                 }
               }}
               onCloseDialog={() => {
                 if (onClose) {
                   onClose()
                 }
               }}
             />
             </Grid>
            )}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: 0 }}>
            <CardContent sx={{ padding: 0 }}>
              <Box
                component="form"
                sx={{
                  mt: 1,
                  pl: 0
                }}
                noValidate
                autoComplete="off"
              >
                {!isQuotation && billId != null && billId > 0 && (
                  <ChallanSection
                    challanNo={null}
                    userDefinedChallanNumber={userDefinedChallanNumber}
                    setDefinedChallanNumber={setDefinedChallanNumber}
                    selectedChallan={selectedChallan}
                    setSelectedChallan={setSelectedChallan}
                    bill_id={billId}
                    setReadyToSubmit={setReadyToSubmit}
                    datePickerOpen={datePickerOpen}
                    setDatePickerOpen={setDatePickerOpen}
                    date={date}
                    setDate={setDate}
                    singleRow={true}
                    challan_type={null}
                    mdValue={0}
                  />
                )}
              </Box>
              <Grid container spacing={2} sx={{ mt: 2, mb: 4, width: '100%' }} alignItems="flex-start">
                <Grid item sx={{ flex: 1, minWidth: 0 }}>
                  <Autocomplete
                    size="small"
                    id="select-charge-type"
                    value={chargeType}
                    options={chargeTypes}
                    onChange={(e, newValue) => {
                      if (newValue === chargeTypes[0]) {
                        setDummyCharge(null)
                      } else {
                        setDummyCharge({
                          amount: 0,
                          chargeTitle: newValue,
                          itemType: null,
                        })
                      }
                      setChargeType(newValue)
                    }}
                    renderInput={(params) => (
                      <TextField
                        size="small"
                        {...params}
                        label="Select Charge Type"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <AddOtherCharges
                  dummyCharge={dummyCharge}
                  isGSTEnabled={isGSTEnabled}
                  enableWeightFields={chargeType === MATERIAL_LOSS_CHARGE_LABEL}
                  addOtherCharge={(charge) => {
                    if (chargeType === MATERIAL_LOSS_CHARGE_LABEL) {
                      charge.itemType = WEIGHT_CHARGE_ITEM_TYPE
                    } else {
                      charge.itemType =
                        chargeType === chargeTypes[0]
                          ? null
                          : chargeType?.replace(' ', '_')?.toUpperCase()
                    }
                    setCharges(charges.concat(charge))
                  }}
                  singleRow
                />
              </Grid>

              <Box component="form" noValidate autoComplete="off">
                {charges.map((item, id) => {
                  return (
                    <Fragment key={id}>
                      {id >= 0 && <Divider />}
                      <PreviewAddOtherCharges
                        charge={item}
                        deleteCharge={() =>
                          setCharges(charges.filter((c, i) => i !== id))
                        }
                        key={id}
                      />
                    </Fragment>
                  )
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </AppDialog>
  )
}

export default OtherCharges
